// Display formatting shared by the subscriber cards — the React port of
// search.js's fmtAgo / fmtRosUptime / rx-power colouring.

/** Parse a RouterOS uptime string ("1w2d3h4m5s") to seconds. Mirrors
 *  search.js's parseUptime — sums every `<n><w|d|h|m|s>` token; anything
 *  else is ignored. Returns 0 for empty/unparseable. */
export function parseRosUptime(s: string | null | undefined): number {
  if (!s) return 0;
  let t = 0;
  for (const m of s.matchAll(/(\d+)([wdhms])/g)) {
    const v = parseInt(m[1] ?? "0", 10);
    switch (m[2]) {
      case "w": t += v * 604800; break;
      case "d": t += v * 86400; break;
      case "h": t += v * 3600; break;
      case "m": t += v * 60; break;
      case "s": t += v; break;
    }
  }
  return t;
}

/** Seconds → "11d 18h 25m 21s" (no weeks; days roll up indefinitely). */
export function fmtUptimeSeconds(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (d || h) parts.push(`${h}h`);
  parts.push(`${m}m`, `${sec}s`);
  return parts.join(" ");
}

/** A timestamp ("YYYY-MM-DD H:M:S" with `-` or `:` separators, or RFC3339)
 *  → "3 Hours 25 Minutes ago" / "11 Days 18 Hours ago" / "42 seconds ago".
 *  Falls back to the raw string if it can't be parsed. */
export function fmtAgo(raw: string | null | undefined): string {
  if (!raw) return "—";
  const norm = raw.replace(
    /(\d{4})[-:](\d{2})[-:](\d{2})[ T](\d{1,2}):(\d{2}):(\d{2})/,
    (_m, y: string, mo: string, d: string, h: string, mi: string, se: string) =>
      `${y}-${mo}-${d}T${h.padStart(2, "0")}:${mi}:${se}`,
  );
  const dt = new Date(norm);
  if (isNaN(dt.getTime())) return raw;
  const sec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (sec < 60) return `${sec} second${sec === 1 ? "" : "s"} ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} Minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  const rm = min % 60;
  if (hr < 24) return `${hr} Hour${hr === 1 ? "" : "s"}${rm ? ` ${rm} Minute${rm === 1 ? "" : "s"}` : ""} ago`;
  const dy = Math.floor(hr / 24);
  const rh = hr % 24;
  return `${dy} Day${dy === 1 ? "" : "s"}${rh ? ` ${rh} Hour${rh === 1 ? "" : "s"}` : ""} ago`;
}

/** Bits per second → "17.9 Kbps" / "12.3 Mbps" / "0 bps". */
export function fmtRate(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !isFinite(bps)) return "—";
  if (bps === 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** Tailwind text colour for an ONU RX power (dBm), against the GPON
 *  optical budget for a Class B+ ONU receiver:
 *    • above ≈ -8 dBm → red (receiver overload — too much light, e.g. the
 *      ONU is too close to the OLT or a splitter is missing)
 *    • -8 … -25 dBm   → green (healthy)
 *    • -25 … -27 dBm  → amber (marginal — within ~2 dB of the ≈ -27 dBm
 *      receiver-sensitivity floor)
 *    • ≤ -27 dBm      → red (below sensitivity → bit errors / no link)
 *    • 0 / blank / NaN → muted ("no light" / unknown)
 *  Stricter than the PHP `rxClass`, which had no high-end overload check. */
export function rxPowerToneClass(rxDbm: unknown): string {
  const v = parseFloat(String(rxDbm ?? ""));
  if (!isFinite(v) || v === 0) return "text-muted-foreground";
  if (v > -8) return "text-destructive"; // receiver overload — too hot
  if (v > -25) return "text-success"; // healthy
  if (v > -27) return "text-warning"; // marginal — near the sensitivity floor
  return "text-destructive"; // below ONU receiver sensitivity
}
